import { Switch, Route } from "react-router-dom";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ProtectedRoute from "./ProtectedRoute";
import App from "../App";
import AuthPage from "../Auth/AuthPage";
import AuthSAML from "../Auth/AuthSAML/AuthSAML";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import ErrorFallback from "../Components/ErrorFallback/ErrorFallback";
import AppPreferencesProvider from "../General/AppPreferencesProvider";
import {
  ApolloLink,
  from,
  useApolloClient,
  createHttpLink,
  type ApolloQueryResult,
  type NormalizedCacheObject,
  type Operation,
  type RefetchQueriesOptions,
  type RefetchQueriesResult,
} from "@apollo/client";
import type { ApolloCache } from "@apollo/client/cache";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import useLogger, { type LogErrorInfo } from "../Logger/useLogger";
import { AuthContext, CONTEXT_KEY } from "../Auth/AuthContext";
import { useSsoFailureListener } from "../Auth/useSsoFailureListener";
import type { AuthState, AuthUser } from "../types/AuthProvider";
import { showDialog, useMutationWrapper } from "@SeemplicityDev/shared";
import { useTranslation } from "react-i18next";
import { setContext } from "@apollo/client/link/context";
import {
  UPDATE_LAST_LOGIN,
  getAuthenticatedUser,
  getCurrentSession,
} from "../Auth/AuthApi";
import FindingRawData from "../Findings/FindingDetailsPane/FindingsDetailsPaneComponents/AdditionalData/FindingRawData";
import { isAmplify, isLocalhost, isDevEnv } from "../utils";
import { apiConfig } from "../API/config";
import { generateOpId, isPermissionError } from "../API/utils/authUtils";

// Mirrors the default TResult of ApolloClient.refetchQueries.
type RefetchQueriesPromiseResult = Promise<ApolloQueryResult<unknown>>;

const neededCacheInvalidationQueries = new Set<string>();

const DATA_VALIDATION_FINDINGS_OPERATION = "GetDataValidationFindings";

// Apollo's NetworkError union (Error | ServerError | ServerParseError | null)
// does not surface statusCode/result/graphQLErrors on every member; the error
// link reads these defensively from whichever shape arrived at runtime.
type NetworkErrorWithDetails = Error & {
  statusCode?: number;
  result?: unknown;
  graphQLErrors?: ReadonlyArray<{ message?: string }>;
};

// react-error-boundary passes a plain Error; chunk-load failures are tagged at
// runtime with this custom flag (see dynamic-import deployment handling).
type ErrorBoundaryError = Error & { isChunkLoadError?: boolean };

const verifyRequestVariables = (
  variablesObject: Operation["variables"]
): string | null => {
  try {
    return JSON.stringify(variablesObject);
  } catch (e) {}
  return null;
};

const AppRouter: React.FC = () => {
  const [loaded, setLoaded] = useState(false);
  const { logWarning, logError } = useLogger();
  const { t } = useTranslation();
  const {
    authState: authStateFromContext,
    populateAuthentication,
    stopImpersonation,
    logout,
  } = useContext(AuthContext);
  const client = useApolloClient();
  const authRef = useRef<AuthState>();
  const [updateLastLogin] = useMutationWrapper(UPDATE_LAST_LOGIN);
  // Armed before the bootstrap effect below, so an IdP failure it triggers is
  // recorded and cannot start an app -> IdP -> app loop.
  useSsoFailureListener();

  authRef.current = authStateFromContext;

  const originalRefetchQueries = client.refetchQueries;
  // Wrap client.refetchQueries to capture invalidation targets. The wrapper is
  // typed loosely (matching the runtime, which also accepts a bare array of
  // query names) and cast back to the strict method type on assignment.
  client.refetchQueries = function (
    this: typeof client,
    options: RefetchQueriesOptions<
      ApolloCache<NormalizedCacheObject>,
      RefetchQueriesPromiseResult
    >
  ): RefetchQueriesResult<RefetchQueriesPromiseResult> {
    let normalizedOptions = options;
    if (Array.isArray(normalizedOptions)) {
      normalizedOptions = {
        include: normalizedOptions,
      } as RefetchQueriesOptions<
        ApolloCache<NormalizedCacheObject>,
        RefetchQueriesPromiseResult
      >;
    }
    if (normalizedOptions.include) {
      (normalizedOptions.include as ReadonlyArray<string>).forEach((query) => {
        neededCacheInvalidationQueries.add(query);
      });
    }
    // `.call` cannot infer the method's TResult generic, so the result widens
    // to RefetchQueriesResult<unknown>; cast back to the declared return type.
    return originalRefetchQueries.call(
      this,
      normalizedOptions
    ) as RefetchQueriesResult<RefetchQueriesPromiseResult>;
  } as typeof client.refetchQueries;

  const errorMiddleware = useMemo(
    () =>
      onError(({ networkError, operation, graphQLErrors, response }) => {
        const opId = operation?.getContext()?.headers?.["op-id"];
        const netError = networkError as NetworkErrorWithDetails | null;
        if (netError) {
          if (isAmplify || isLocalhost || isDevEnv) {
            return;
          }

          if (netError.statusCode === 401 || netError.statusCode === 403) {
            // WAK-33496: a 403 with a "permission denied" body comes from a
            // backend authz check (e.g. /ai-agents for a user without that
            // entitlement). The session is still valid — do NOT log the user
            // out. Only structured JSON payloads are inspected (not raw body
            // text) to avoid matching generic proxy/CDN "403 Forbidden" pages.
            // response carries GraphQL errors[].message; networkError.result
            // carries the parsed REST body (e.g. { "detail": "Insufficient
            // permissions" }).
            const isResourcePermissionDenial =
              netError.statusCode === 403 &&
              (isPermissionError(response) ||
                isPermissionError(netError?.result));

            if (!isResourcePermissionDenial) {
              if (authRef.current?.[CONTEXT_KEY]?.id) {
                stopImpersonation();
                // This only appears here, as it will set off the "session expired" modal.
                // In the other flows, there's no need to show that modal, hence no need to
                // dispatch this event.
                window.dispatchEvent(
                  new StorageEvent("storage", { key: CONTEXT_KEY })
                );
              } else if (!!authRef.current?.user) {
                // Log the user out, as they can't access the platform. Not
                // user-initiated: this is an expired session, so the SSO
                // auto-redirect must stay armed.
                logout({ userInitiated: false });
                return;
              }
            }
          }

          if (operation?.operationName === "createLogs") {
            console.log("createLogs: network error");
          } else {
            const isExpectedTimeout =
              netError?.statusCode === 504 &&
              operation?.operationName === DATA_VALIDATION_FINDINGS_OPERATION;
            if (isExpectedTimeout) {
              return;
            }

            const variables = verifyRequestVariables(operation?.variables);
            logWarning(
              new Error(netError as unknown as string),
              `network error`,
              {
                query: operation?.operationName,
                variables,
              } as unknown as LogErrorInfo
            );

            const message =
              netError?.message ||
              netError?.graphQLErrors?.[0]?.message ||
              "GraphQL Error";

            const sentryError = new Error(message);

            logError(
              new Error(sentryError as unknown as string),
              `Network Error: ${message}`,
              {
                tags: { operationName: operation.operationName, opId },
                extra: {
                  error: netError?.message,
                  operation,
                  variables,
                  graphql: netError,
                },
              }
            );
          }
        } else if (response?.errors) {
          response.errors.forEach((error) => {
            // In this branch `netError` is null, so TS narrows it to `never`
            // after the optional chain. Re-broaden to its declared type to read
            // these defensively-optional fields; runtime is unchanged (null?.x
            // is undefined either way).
            const narrowedNetError = netError as NetworkErrorWithDetails | null;
            const message =
              narrowedNetError?.message ||
              narrowedNetError?.graphQLErrors?.[0]?.message ||
              "GraphQL Error";

            const sentryError = new Error(message);
            logError(
              new Error(sentryError as unknown as string),
              `GraphQL Error: ${error.message}`,
              {
                tags: { operationName: operation.operationName, opId },
                extra: { error, operation },
              }
            );
          });
        }
      }),
    [authRef, logout, logWarning, stopImpersonation]
  );

  const roundTripMiddleware = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        operation.setContext({ start: new Date() });

        return forward(operation).map((data) => {
          const time =
            new Date().getTime() - operation.getContext().start.getTime();
          if (time >= 10000) {
            if (operation?.operationName === "createLogs") {
              console.log("createLogs: slow query - 10+ seconds");
            }
            // else {
            // const variables = verifyRequestVariables(operation?.variables);
            // logWarning(null, "slow query - 10+ seconds", {
            //   time_took: time * 0.001,
            //   query: operation?.operationName,
            //   variables,
            // });
            // }
          }

          return data;
        });
      }),
    [logWarning]
  );

  const markNeededCacheInvalidationQueries = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        const refetchQueries = operation.getContext().refetchQueries as
          | ReadonlyArray<string>
          | undefined;
        if (refetchQueries) {
          refetchQueries.forEach((query) => {
            neededCacheInvalidationQueries.add(query);
          });
        }
        return forward(operation);
      }),
    []
  );

  const cacheInvalidationLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        if (neededCacheInvalidationQueries.has(operation?.operationName)) {
          operation.setContext(
            ({ headers = {} }: { headers?: Record<string, string> }) => ({
              headers: {
                ...headers,
                "x-cache-invalidate": "true",
              },
            })
          );
          // removing from neededCacheInvalidationQueries:
          setTimeout(
            () =>
              neededCacheInvalidationQueries.delete(operation?.operationName),
            1000
          );
        }
        return forward(operation);
      }),
    []
  );

  const retryLink = useMemo(
    () =>
      new RetryLink({
        delay: { initial: 300, max: 3000, jitter: true },
        attempts: {
          max: 3,
          retryIf: (error) => error instanceof TypeError,
        },
      }),
    []
  );

  const httpLink = useMemo(
    () =>
      createHttpLink({
        uri: (request) => {
          const requestCtx = request.getContext();
          const isCNC =
            !!authRef.current?.[CONTEXT_KEY]?.id ||
            !!requestCtx?.impersonation?.id;
          return isCNC
            ? apiConfig.GRAPHQL_SERVER_CNC
            : apiConfig.GRAPHQL_SERVER;
        },
      }),
    [authRef]
  );

  const setAuthorizationLink = useMemo(
    () =>
      setContext((request, requestCtx) => {
        return getCurrentSession()
          .then((data) => {
            const token = data.getAccessToken().getJwtToken();
            const headers: Record<string, string> = {
              ...requestCtx?.headers,
              Authorization: `Bearer ${token}`,
            };
            if (!!authRef.current?.account) {
              headers["X-Account"] = authRef.current.account.id;
            }

            const context =
              authRef.current?.[CONTEXT_KEY] || requestCtx?.impersonation;
            if (!!context?.id) {
              headers["X-Account"] = context.id;
            }

            // Add current page URL header
            if (typeof window !== "undefined") {
              headers["x-page-url"] = window?.location?.href;
            }

            headers["op-id"] = requestCtx?.headers?.["op-id"] ?? generateOpId();

            return {
              headers,
            };
          })
          .catch((err) => {
            console.log("Error getting user token ", err);
            return { headers: {} };
          });
      }),
    [authRef]
  );

  useEffect(() => {
    client.setLink(
      from([
        errorMiddleware,
        roundTripMiddleware,
        retryLink,
        setAuthorizationLink,
        markNeededCacheInvalidationQueries,
        cacheInvalidationLink,
        httpLink,
      ])
    );
  }, [
    client,
    errorMiddleware,
    markNeededCacheInvalidationQueries,
    cacheInvalidationLink,
    httpLink,
    roundTripMiddleware,
    retryLink,
    setAuthorizationLink,
  ]);

  useEffect(() => {
    const fn = (event: StorageEvent) => {
      if (event.key === CONTEXT_KEY) {
        try {
          client.stop();
          client.clearStore().finally(() => {
            showDialog({
              showCloseIcon: false,
              title: t("Session expired"),
              message: (
                <div className={"user-logged-out-msg"}>
                  {t(
                    "You have been logged out.\nClick Refresh to access your system."
                  )}
                </div>
              ),
              buttons: [
                {
                  "data-testid": "refresh-window",
                  label: t("Refresh"),
                  onClick: () => {
                    window.location.reload();
                  },
                },
              ],
            });
          });
        } catch {}
      }
    };
    window.addEventListener("storage", fn);
    return () => window.removeEventListener("storage", fn);
  }, [t, client]);

  // Try to authenticate when the page initially loads
  useEffect(() => {
    getAuthenticatedUser()
      .then((cognitoUser) => {
        // getAuthenticatedUser resolves the aws-amplify CognitoUser; AuthUser
        // is the app-level view of that same runtime object.
        const authUser = cognitoUser as unknown as AuthUser;
        return populateAuthentication(
          authUser,
          (authUser as { preferredMFA?: string })?.preferredMFA
        );
      }) // If there's a user - we're logged in
      .catch((err) => {
        // Clear stale Cognito tokens from localStorage to avoid zombie session state
        // where Amplify thinks there might be a session but refresh token is expired.
        Object.keys(window.localStorage)
          .filter((key) => key.startsWith("CognitoIdentityServiceProvider"))
          .forEach((key) => window.localStorage.removeItem(key));
        stopImpersonation();
      })
      .finally(() => setLoaded(true));
  }, [populateAuthentication, stopImpersonation, setLoaded]);

  useEffect(() => {
    if (loaded && authRef.current?.authenticated) {
      updateLastLogin();
    }
  }, [loaded, authRef.current?.authenticated, updateLastLogin]);

  if (!loaded) {
    return null;
  }

  return (
    <ErrorBoundary
      onError={(error: ErrorBoundaryError, info) => {
        if (!!authRef.current?.user) {
          if (error.isChunkLoadError) {
            // Chunk load failures are graceful deployment-related errors, not app crashes.
            // Log as a regular Sentry error without the error-boundary classification.
            logError(error, `Chunk load failed: ${error?.message}`, {
              tags: { error_type: "chunk-load-error" },
            });
          } else {
            logError(error, `Error Boundary: ${error?.message}`, {
              extra: {
                componentStack: info.componentStack,
                error_type: "error-boundary",
              },
              tags: {
                error_type: "error-boundary",
              },
            });
          }
        }
      }}
      fallbackRender={(props: FallbackProps) => (
        <div
          style={{
            width: "100vw",
            height: "100vh",
          }}
        >
          <a className={"app-logo"} href={"/"} target="_self"></a>
          <ErrorFallback {...props} type={"static"} />
        </div>
      )}
    >
      <Switch>
        <Route exact path="/login">
          <AuthPage />
        </Route>
        <Route path="/saml/:provider?">
          <AuthSAML />
        </Route>
        <Route path="/changePassword">
          <AuthPage />
        </Route>
        <Route path="/mfa">
          <AuthPage />
        </Route>
        <Route path="/logout">
          <AuthPage />
        </Route>
        <ProtectedRoute path="/findingRaw/:id?">
          <FindingRawData />
        </ProtectedRoute>
        <ProtectedRoute path="/:item/:id?">
          <AppPreferencesProvider>
            <App />
          </AppPreferencesProvider>
        </ProtectedRoute>
        <Route path="/">
          <AuthPage />
        </Route>
      </Switch>
    </ErrorBoundary>
  );
};

export default AppRouter;
